// Pure display helpers for Churn & Recovery + Customer Insights.
// Colors come from design.md's status-badge palette.
import moment from "moment";

export const CADENCE_COLORS = {
  green: "#16A34A",
  yellow: "#F59E0B",
  red: "#EF4444",
  none: "#9CA3AF",
};

export const SEGMENT_META = {
  active: { label: "Active", color: "#16A34A" },
  at_risk: { label: "At-risk", color: "#F59E0B" },
  dormant: { label: "Dormant", color: "#EF4444" },
  never: { label: "Never visited", color: "#0EA5E9" },
};

// Dormant boundary personalized to the client's own rhythm (spec
// 2026-07-25-rebooking-status): long-cadence clients (e.g. annual filler)
// aren't written off at a flat 180 days. Backend must mirror this rule.
export const dormantThreshold = (customer) => {
  const cadenceDays = Number(customer?.cadence_days);
  if (!cadenceDays || !(customer?.visit_count >= 3)) return 180;
  return Math.max(180, Math.min(2 * cadenceDays, 365));
};

// Fixed clinic rebooking rules per treatment category (spec
// 2026-07-29-treatment-windows). Matched against the service CATEGORY name
// first (PRP, Laser Hair Removal and Injectables are separate categories in
// the clinic's category tree); service-name keywords are only a fallback for
// bookings with no category. Laravel mirrors this table.
export const TREATMENT_WINDOWS = [
  {
    key: "laser",
    label: "Laser Hair Removal",
    short: "Laser",
    window_days: 45,
    categoryMatch: /laser/i,
    serviceMatch: /laser/i,
  },
  {
    key: "promo",
    label: "Promo",
    short: "Promo",
    window_days: 45,
    categoryMatch: /promo/i,
    serviceMatch: /promo/i,
  },
  {
    key: "injectables",
    label: "Injectables",
    short: "Injectables",
    window_days: 180,
    categoryMatch: /inject/i,
    serviceMatch: /botox|filler|dysport|inject/i,
  },
  {
    key: "prp",
    label: "PRP",
    short: "PRP",
    window_days: 180,
    categoryMatch: /prp/i,
    serviceMatch: /prp/i,
  },
];

export const treatmentShortLabel = (w) =>
  TREATMENT_WINDOWS.find((r) => r.key === w?.category_key)?.short ||
  w?.category_label ||
  "";

export const matchTreatmentCategory = (categoryName, serviceName) => {
  if (categoryName) {
    return TREATMENT_WINDOWS.find((r) => r.categoryMatch.test(categoryName)) || null;
  }
  if (serviceName) {
    return TREATMENT_WINDOWS.find((r) => r.serviceMatch.test(serviceName)) || null;
  }
  return null;
};

// Status of one treatment_windows entry vs today. Same multiplier semantics
// as the cadence dot: yellow strictly past 1x window since the last category
// visit, red strictly past 1.5x, green when due within the next 7 days.
// Null when the window isn't actionable yet (or the entry is malformed).
export const treatmentWindowStatus = (w) => {
  const windowDays = Number(w?.window_days);
  const last = moment(w?.last_date);
  const due = moment(w?.due_date);
  if (!windowDays || !last.isValid() || !due.isValid()) return null;

  const today = moment().startOf("day");
  const quietDays = today.diff(last.startOf("day"), "days");
  const daysPastDue = today.diff(due.startOf("day"), "days");

  if (daysPastDue > 0) {
    const way = quietDays > windowDays * 1.5;
    return {
      level: way ? "way_overdue" : "overdue",
      color: way ? CADENCE_COLORS.red : CADENCE_COLORS.yellow,
      daysPastDue,
    };
  }
  if (-daysPastDue <= 7) {
    return { level: "due_soon", color: CADENCE_COLORS.green, daysPastDue };
  }
  return null;
};

// Earliest due date across the learned cadence and every treatment window —
// drives the card's "Expected next" row. Cadence participates whenever an
// expected_date exists (display is not gated on the 3-visit minimum; the
// badge severity is). Returns { date, driver } or null; driver is "cadence"
// or the treatment's category_label.
export const nextDue = (data) => {
  const candidates = [];
  if (data?.cadence?.expected_date) {
    candidates.push({ date: data.cadence.expected_date, driver: "cadence" });
  }
  (data?.treatment_windows || []).forEach((w) => {
    if (w?.due_date && moment(w.due_date).isValid()) {
      candidates.push({ date: w.due_date, driver: w.category_label, window: w });
    }
  });
  if (!candidates.length) return null;
  return candidates.reduce((a, b) => (moment(b.date).isBefore(a.date) ? b : a));
};

// Plain-words rebooking status — blends the client's OWN learned cadence with
// the fixed treatment-category windows (spec 2026-07-29-treatment-windows).
// The earliest-due firing signal names the badge ("Laser Hair Removal overdue
// · 21d past due"); the color is the most severe firing signal. The 3-visit
// minimum still gates the cadence signal only — one PRP visit 6 months ago is
// a real flag on its own.
export const rebookingStatus = (data) => {
  const today = moment().startOf("day");
  const candidates = [];

  const extras = [];
  const pct = data?.spend_trend?.pct_change;
  if (typeof pct === "number" && pct <= -20) {
    extras.push(`spend down ${Math.abs(Math.round(pct))}%`);
  }
  const freq = data?.frequency_trend;
  if (
    typeof freq?.recent_per_90d === "number" &&
    typeof freq?.alltime_per_90d === "number" &&
    freq.alltime_per_90d > 0 &&
    freq.recent_per_90d < freq.alltime_per_90d * 0.7
  ) {
    extras.push("booking pace below their norm");
  }

  // Signal 1: whole-client cadence (trustworthy rhythm only — 3+ visits).
  const cadence = data?.cadence;
  const cadenceDays = Number(cadence?.cadence_days);
  const lastVisit = moment(cadence?.last_visit_date);
  if (cadenceDays && data?.maturity?.visit_count >= 3 && lastVisit.isValid()) {
    const quietDays = today.diff(lastVisit.startOf("day"), "days");
    const dueDate =
      cadence.expected_date ||
      lastVisit.clone().add(cadenceDays, "days").format("YYYY-MM-DD");
    if (quietDays > cadenceDays * 1.5) {
      candidates.push({
        level: "way_overdue",
        color: CADENCE_COLORS.red,
        label: `Way overdue · ${quietDays}d quiet`,
        dueDate,
      });
    } else if (quietDays > cadenceDays) {
      candidates.push({
        level: "overdue",
        color: CADENCE_COLORS.yellow,
        label: `Overdue · ${quietDays}d quiet`,
        dueDate,
      });
    } else if (cadence.expected_date) {
      const daysToExpected = moment(cadence.expected_date)
        .startOf("day")
        .diff(today, "days");
      if (daysToExpected >= 0 && daysToExpected <= 7) {
        candidates.push({
          level: "due_soon",
          color: CADENCE_COLORS.green,
          label: `Due soon · expected ${formatDate(cadence.expected_date)}`,
          dueDate: cadence.expected_date,
        });
      }
    }
  }

  // Signal 2: fixed treatment-category windows (no visit minimum).
  (data?.treatment_windows || []).forEach((w) => {
    const s = treatmentWindowStatus(w);
    if (!s) return;
    candidates.push({
      level: s.level,
      color: s.color,
      dueDate: w.due_date,
      label:
        s.level === "due_soon"
          ? `${w.category_label} due · expected ${formatDate(w.due_date)}`
          : `${w.category_label} ${
              s.level === "way_overdue" ? "way overdue" : "overdue"
            } · ${s.daysPastDue}d past due`,
    });
  });

  if (!candidates.length) return null;

  const SEVERITY = { due_soon: 1, overdue: 2, way_overdue: 3 };
  // Overdue signals outrank due-soon; among those, the earliest due date is
  // the most actionable and names the badge.
  const pool = candidates.some((c) => c.level !== "due_soon")
    ? candidates.filter((c) => c.level !== "due_soon")
    : candidates;
  const driver = pool.reduce((a, b) =>
    moment(b.dueDate).isBefore(a.dueDate) ? b : a
  );
  const worst = candidates.reduce((a, b) =>
    SEVERITY[b.level] > SEVERITY[a.level] ? b : a
  );
  return { level: worst.level, color: worst.color, label: driver.label, extras };
};

// Mirrors CRMPage's customer_batch pill logic exactly (same labels/colors
// as the CRM customer list) so status reads identically on both pages.
export const batchMeta = (batch) => {
  const status = batch || "New User";
  const isCancel = typeof status === "string" && status.includes("Expire/Cancel");
  const isDeclined = typeof status === "string" && status.includes("Declined");
  const isMember = !isCancel && !isDeclined && status !== "New User";
  return {
    label: status,
    color: isCancel ? "#EF4444" : isDeclined ? "#F59E0B" : isMember ? "#16A34A" : "#0EA5E9",
  };
};

export const formatDate = (date) => (date ? moment(date).format("MMM D, YYYY") : "");

export const formatCadenceLine = (customer) => {
  const visits = customer?.visit_count ?? 0;
  if (!customer?.cadence_days || customer?.cadence_status === "none") {
    if (visits === 0) return "No visits yet";
    return `No cadence yet · ${visits} visit${visits === 1 ? "" : "s"}`;
  }
  return `Books every ~${Math.round(customer.cadence_days)}d · last ${formatDate(
    customer.last_visit_date
  )} · expected ${formatDate(customer.expected_date)}`;
};

export const spendTrendCopy = (trend) => {
  if (!trend) return null;
  const pct = Math.round(trend.pct_change);
  if (pct <= -20) return `Spend down ${Math.abs(pct)}% — reach out and check what changed.`;
  if (pct >= 20) return `Spend up ${pct}% — great momentum, look for upsell openings.`;
  return "Spend is steady — maintain the current rhythm.";
};

export const frequencyTrendCopy = (trend) => {
  if (!trend) return null;
  if (trend.recent_per_90d < trend.alltime_per_90d * 0.7) {
    return "Booking pace has slowed vs their usual — a proactive call may re-engage them.";
  }
  return "Booking pace is holding steady.";
};

export const upsellCopy = (u) => {
  if (!u) return null;
  return `${u.percentage}% of customers who book ${u.have_service} also book ${u.suggest_service} — this customer hasn't yet.`;
};

export const cadenceCopy = (cadence) => {
  if (!cadence?.cadence_days) return null;
  if (cadence.cadence_status === "green") {
    return `Next visit expected around ${formatDate(
      cadence.expected_date
    )} — time a touchpoint a few days before.`;
  }
  return "Overdue for their usual rebooking — time a proactive touchpoint now.";
};

/* ---- Last touchpoints: the "when did we last touch this customer" pair ----
   Both helpers accept the churn-row shape ({ type, date, actor_name }) as well
   as a contact_log / insights row ({ created_at, summary }), so the same copy
   drives the Insights tab and the chat sidebar. */

export const CONTACT_TYPE_LABELS = {
  call: "Call",
  text: "Text",
  sms: "SMS",
  email: "Email",
  in_person: "In-person visit",
  note: "Note",
  other: "Contact",
};

export const contactTypeLabel = (type) =>
  CONTACT_TYPE_LABELS[type] || (type ? String(type).replace(/_/g, " ") : "Contact");

// Whole days between `date` and today in the local day frame. Positive = past.
// Null when the date is missing or unparseable.
export const daysSince = (date) => {
  if (!date) return null;
  const d = moment(date);
  if (!d.isValid()) return null;
  return moment().startOf("day").diff(d.clone().startOf("day"), "days");
};

export const relativeDayLabel = (date) => {
  const days = daysSince(date);
  if (days === null) return "";
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 0) return `in ${Math.abs(days)}d`;
  return `${days}d ago`;
};

// Staleness of the last logged outreach. Severity ladder matches the cadence
// dot's intent: fresh (<=7d) green, warm (<=30d) blue, cooling (<=90d) amber,
// cold red — so "we already texted them yesterday" reads before any nudge.
export const lastContactMeta = (contact) => {
  const date = contact?.date || contact?.created_at || null;
  const days = daysSince(date);
  if (days === null) {
    return {
      color: CADENCE_COLORS.none,
      headline: "No outreach logged yet",
      detail: null,
      action:
        "Nothing on the contact log — log the first call or text so whoever picks this up next knows where things stand.",
      stale: true,
    };
  }
  const color =
    days <= 7 ? "#16A34A" : days <= 30 ? "#0EA5E9" : days <= 90 ? "#F59E0B" : "#EF4444";
  const detail = [
    contact.actor_name ? `${formatDate(date)} by ${contact.actor_name}` : formatDate(date),
    contact.summary,
  ]
    .filter(Boolean)
    .join(" · ");
  return {
    color,
    headline: `${contactTypeLabel(contact.type)} ${relativeDayLabel(date)}`,
    detail: detail || null,
    action:
      days <= 7
        ? "Just reached out — give it a few days before the next touchpoint."
        : days <= 30
        ? "Recent enough to reference — open with what was discussed last time."
        : "Outreach has gone cold — a personal message lands better than another generic nudge.",
    stale: days > 30,
  };
};

// Recency of the last completed visit. Accepts the insights `last_booking`
// object, or a cadence-shaped fallback ({ last_visit_date }) for payloads that
// predate the field.
export const lastBookingMeta = (booking) => {
  const date = booking?.date || booking?.last_visit_date || null;
  const days = daysSince(date);
  if (days === null) {
    return {
      color: CADENCE_COLORS.none,
      headline: "No visits yet",
      detail: null,
      action: "Never been in — a first-visit offer is the play, not a rebooking nudge.",
    };
  }
  const color =
    days <= 30 ? "#16A34A" : days <= 90 ? "#0EA5E9" : days <= 180 ? "#F59E0B" : "#EF4444";
  const detail = [
    booking?.service,
    booking?.time,
    booking?.staff_name ? `with ${booking.staff_name}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return {
    color,
    headline: `${formatDate(date)} · ${relativeDayLabel(date)}`,
    detail: detail || null,
    action:
      days <= 30
        ? "Recently in — check how the treatment settled and pre-book the next one."
        : days <= 90
        ? "Still warm — reference the service they last booked when you reach out."
        : days <= 180
        ? "Quiet for a while — lead with their last service, it's the easiest yes."
        : "Over six months quiet — treat this as a win-back, not a routine rebooking.",
  };
};

export const walletNudgeCopy = (balance) =>
  `$${Number(balance).toFixed(2)} sitting unused on their wallet — the cheapest win-back offer you have.`;

export const membershipCopy = (m) => {
  if (!m || m.status === "none") return null;
  if (m.status === "cancelled") {
    return { color: "#EF4444", text: "Membership cancelled — treat as a win-back target." };
  }
  if (m.status === "declined") {
    const days = m.declined_date ? moment().diff(moment(m.declined_date), "days") : null;
    return {
      color: "#EF4444",
      text: `Card declined${days != null ? ` ${days}d ago` : ""} — reach out before dunning ends.`,
    };
  }
  const cycleAge = m.start_date ? moment().diff(moment(m.start_date), "days") % 30 : null;
  if ((m.visits_this_cycle ?? null) === 0 && (cycleAge == null || cycleAge >= 14)) {
    return {
      color: "#F59E0B",
      text: `Paying $${m.price}/mo with no visits this cycle — silent churn risk.`,
    };
  }
  if (m.renewal_date && moment(m.renewal_date).diff(moment(), "days") <= 14) {
    return {
      color: "#A72C68",
      text: `Renews ${formatDate(m.renewal_date)} — confirm they're happy before the charge.`,
    };
  }
  return { color: "#16A34A", text: "Membership healthy — visits and billing on track." };
};

export const serviceWindowLabel = (w) =>
  `${w.service} — due ${formatDate(w.due_date)}`;
